import {Injectable} from '@angular/core';
import {MessageService} from 'primeng/api';
import {groupBy, mergeMap, Subject, throttleTime, timer} from 'rxjs';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

export type ToastPosition = 'top-center' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'bottom-center' | 'center';
export type ToastSeverity = 'success' | 'info' | 'error' | 'warn';

@UntilDestroy()
@Injectable({
    providedIn: 'root'
})
export class ToastService {

    public static readonly time = 5000;
    private readonly errorThrottlingSubject = new Subject<{ title: string, message: string, position?: ToastPosition }>();
    private readonly destroy$ = new Subject<void>();

    constructor(private messageService: MessageService) {
        this.errorThrottlingSubject.pipe(
            untilDestroyed(this),
            groupBy(
                ({title, message}) => `${title}-${message}`,
                {duration: group => timer(ToastService.time)}
            ),
            mergeMap(group => group.pipe(throttleTime(ToastService.time)))
        ).subscribe(({title, message, position}) => {
            this.message(title, message, 'error', position);
        });
    }

    info(title: string, message: string, position?: ToastPosition) {
        this.message(title, message, 'info', position);
    }

    success(title: string, message: string, position?: ToastPosition) {
        this.message(title, message, 'success', position);
    }

    warning(title: string, message: string, position?: ToastPosition) {
        this.message(title, message, 'warn', position);
    }

    error(title: string, message: string, position?: ToastPosition) {
        this.errorThrottlingSubject.next({title, message, position});
    }

    message(title: string, message: string, type: ToastSeverity,
            position: ToastPosition = 'top-center', time: number = ToastService.time) {
        this.messageService.add({
            key: position,
            severity: type,
            summary: title,
            detail: message,
            life: time
        });
    }

    clear() {
        this.messageService.clear();
    }
}
